import { request } from './client'
import type { MenuItem, PaginatedResponse, VendorEvent } from './types'
import type {
  DiscountFallbackResponse,
  DiscountPreviewInput,
  DiscountPreviewResponse,
  DiscountReportResponse,
  DiscountSchedule,
  DiscountScheduleInput,
  DiscountScope,
  ItemDiscountsResponse,
} from './discount-types'

// The list is paginated (PAGE_SIZE 100). A page of 200 covers almost every
// event in one request; an event with more (archived ones count) takes more.
const LIST_LIMIT = 200
// Every location at the event in one page, as `useVendorEvents` asks for them.
const LOCATION_LIMIT = 1000
// The schedule editor loads every item at the event in one page (the largest
// menus run to about 1,500 items).
const TARGET_ITEM_LIMIT = 5000

/** Every message in a DRF error body, in order. `{code, detail}` gives its detail. */
export function collectMessages(payload: unknown): string[] {
  if (typeof payload === 'string') return payload ? [payload] : []
  if (Array.isArray(payload)) return payload.flatMap(collectMessages)
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    if (typeof record.detail === 'string') return [record.detail]
    return Object.values(record).flatMap(collectMessages)
  }
  return []
}

/**
 * The readable message behind a failed discount request. The endpoints
 * answer validation problems with `{field: [message]}`,
 * `{non_field_errors: [message]}` or `[message]`, and refusals with
 * `{code, detail}`; the messages name what is wrong on their own, so the
 * field keys are dropped.
 */
export function discountErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  const messages = collectMessages((error as { data?: unknown })?.data)
  if (messages.length) return messages.join(' ')
  if (error instanceof Error && error.message) {
    // "400: text" → "text"
    return error.message.replace(/^\d{3}:\s*/, '')
  }
  return fallback
}

export const discountClient = {
  /**
   * Every schedule at the event, page after page until there is no `next`.
   * Archived schedules are left out unless `includeArchived`. Pages run oldest
   * first (the backend's id order), so a schedule created while they load
   * lands on the last page rather than shifting the others; a row seen twice
   * is kept once.
   */
  list: async (token: string, eventId: number, includeArchived = false): Promise<DiscountSchedule[]> => {
    const byId = new Map<number, DiscountSchedule>()
    for (let offset = 0; ;) {
      const page = await request<PaginatedResponse<DiscountSchedule>>('/discount-schedule/', {
        token,
        params: {
          event: eventId,
          include_archived: includeArchived ? 1 : undefined,
          limit: LIST_LIMIT,
          offset: offset || undefined,
        },
      })
      for (const schedule of page.results) byId.set(schedule.id, schedule)
      if (!page.next || !page.results.length) return [...byId.values()]
      offset += page.results.length
    }
  },

  get: (token: string, scheduleId: number): Promise<DiscountSchedule> =>
    request(`/discount-schedule/${scheduleId}/`, { token }),

  /** What the requesting user may do with promotions at the event. */
  scope: (token: string, eventId: number): Promise<DiscountScope> =>
    request('/discount-schedule/scope/', { token, params: { event: eventId } }),

  create: (token: string, data: DiscountScheduleInput): Promise<DiscountSchedule> =>
    request('/discount-schedule/', { method: 'POST', token, body: data }),

  /** Partial. `vendors`, `locations` and `items` replace the stored lists when sent. Pause/resume = `{ is_active }`. */
  update: (token: string, scheduleId: number, data: Partial<DiscountScheduleInput>): Promise<DiscountSchedule> =>
    request(`/discount-schedule/${scheduleId}/`, { method: 'PATCH', token, body: data }),

  /** DELETE archives: sets `archived_at` and `is_active = false`. 204, so nothing comes back. */
  archive: (token: string, scheduleId: number): Promise<void> =>
    request(`/discount-schedule/${scheduleId}/`, { method: 'DELETE', token }),

  /** Prices a draft. A 400 here is an incomplete draft, not a fault, so it isn't reported to error tracking. */
  preview: (token: string, data: DiscountPreviewInput): Promise<DiscountPreviewResponse> =>
    request('/discount-schedule/preview/', { method: 'POST', token, body: data, expectedErrorStatuses: [400] }),

  /** Every schedule that targets the item, and which one is charging now. */
  itemDiscounts: (token: string, itemId: number): Promise<ItemDiscountsResponse> =>
    request(`/vendor-event-item/${itemId}/discounts/`, { token }),

  report: (token: string, eventId: number): Promise<DiscountReportResponse> =>
    request(`/event/${eventId}/discount-report/`, { token }),

  fallbackOrders: (token: string, eventId: number): Promise<DiscountFallbackResponse> =>
    request(`/event/${eventId}/discount-fallback-orders/`, { token }),

  /**
   * The event's locations, each with its vendor, to name targets by. Reading
   * them all (`/vendor-event/`) needs the vendors permission, which a location
   * admin may not hold: on that 403 they get the locations they administer
   * (their own `/vendor-event-administrator/` rows), which are the ones their
   * promotions can target. The 403 stands when they administer none here.
   */
  locations: async (token: string, eventId: number, userId: number | undefined): Promise<VendorEvent[]> => {
    try {
      const page = await request<PaginatedResponse<VendorEvent>>('/vendor-event/', {
        token,
        params: { event: eventId, expand: 'vendor', limit: LOCATION_LIMIT },
        expectedErrorStatuses: [403],
      })
      return page.results
    } catch (error) {
      if ((error as { status?: number }).status !== 403 || userId == null) throw error
      const own = await request<PaginatedResponse<{ vendor_event: VendorEvent }>>('/vendor-event-administrator/', {
        token,
        params: { user: userId, vendor_event__event: eventId, expand: 'vendor_event.vendor', limit: LOCATION_LIMIT },
      })
      if (!own.results.length) throw error
      return own.results.map((row) => row.vendor_event)
    }
  },

  /**
   * Every menu item at the event, NOT expanded: `vendor_event` is the
   * location id. `apiClient.getMenuItems` always expands
   * `vendor_event.vendor`, whose vendor permissions cost queries per item,
   * which is fine for a page of the menu and far too slow for a whole event.
   */
  targetItems: async (token: string, eventId: number): Promise<MenuItem[]> => {
    const page = await request<PaginatedResponse<MenuItem>>('/vendor-event-item/', {
      token,
      params: { vendor_event__event: eventId, limit: TARGET_ITEM_LIMIT },
    })
    return page.results
  },
}
